import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Plus, Pencil, Trash2, Shield, Users, Search, UserPlus, X, KeyRound, Copy, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const MODULES = ['candidates', 'jobs', 'companies', 'clients', 'settings', 'admin', 'skills', 'notifications'] as const;
const ACTIONS = ['create', 'read', 'update', 'delete'] as const;

type Module = typeof MODULES[number];
type Action = typeof ACTIONS[number];

interface CustomRole {
  id: string;
  name: string;
  description: string | null;
  is_system: boolean;
  created_at: string;
}

interface RolePermission {
  id: string;
  role_id: string;
  module: Module;
  action: Action;
}

interface AppUser {
  id: string;
  user_id: string;
  full_name: string;
  email: string;
  role: 'admin' | 'recruiter' | 'analyst';
  is_active: boolean;
  created_at: string;
}

interface UserCustomRole {
  id: string;
  user_id: string;
  role_id: string;
  assigned_at: string;
  custom_role?: CustomRole;
}

const UserRoleManagement = () => {
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<CustomRole[]>([]);
  const [permissions, setPermissions] = useState<RolePermission[]>([]);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [userRoles, setUserRoles] = useState<UserCustomRole[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Role dialog state
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<CustomRole | null>(null);
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  
  // User dialog state
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AppUser | null>(null);
  const [userFormData, setUserFormData] = useState({
    full_name: '',
    role: 'recruiter' as 'admin' | 'recruiter' | 'analyst',
    is_active: true
  });
  
  // Add user dialog state
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [newUserData, setNewUserData] = useState({
    email: '',
    full_name: '',
    role: 'recruiter' as 'admin' | 'recruiter' | 'analyst',
    password: ''
  });
  const [createdUserPassword, setCreatedUserPassword] = useState<string | null>(null);
  
  // Assign role dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  
  // Password reset dialog state
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState<AppUser | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [sendResetEmail, setSendResetEmail] = useState(true);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [passwordCopied, setPasswordCopied] = useState(false);
  
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rolesRes, permsRes, usersRes, userRolesRes] = await Promise.all([
        supabase.from('custom_roles').select('*').order('name'),
        supabase.from('role_permissions').select('*'),
        supabase.from('app_users').select('*').order('created_at', { ascending: false }),
        supabase.from('user_custom_roles').select('*, custom_role:custom_roles(*)')
      ]);

      if (rolesRes.data) setRoles(rolesRes.data);
      if (permsRes.data) setPermissions(permsRes.data as RolePermission[]);
      if (usersRes.data) setUsers(usersRes.data);
      if (userRolesRes.data) setUserRoles(userRolesRes.data as any);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Permission helpers
  const getPermissionKey = (module: Module, action: Action) => `${module}:${action}`;
  
  const hasPermission = (roleId: string, module: Module, action: Action) => {
    return permissions.some(p => p.role_id === roleId && p.module === module && p.action === action);
  };

  const getRolePermissions = (roleId: string) => {
    return permissions.filter(p => p.role_id === roleId);
  };

  const togglePermission = (module: Module, action: Action) => {
    const key = getPermissionKey(module, action);
    const newSet = new Set(selectedPermissions);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    setSelectedPermissions(newSet);
  };

  const toggleModuleAll = (module: Module) => {
    const moduleKeys = ACTIONS.map(a => getPermissionKey(module, a));
    const allSelected = moduleKeys.every(k => selectedPermissions.has(k));
    const newSet = new Set(selectedPermissions);
    
    if (allSelected) {
      moduleKeys.forEach(k => newSet.delete(k));
    } else {
      moduleKeys.forEach(k => newSet.add(k));
    }
    setSelectedPermissions(newSet);
  };

  // Role handlers
  const openNewRole = () => {
    setSelectedRole(null);
    setRoleName('');
    setRoleDescription('');
    setSelectedPermissions(new Set());
    setRoleDialogOpen(true);
  };

  const openEditRole = (role: CustomRole) => {
    setSelectedRole(role);
    setRoleName(role.name);
    setRoleDescription(role.description || '');
    const rolePerms = getRolePermissions(role.id);
    setSelectedPermissions(new Set(rolePerms.map(p => getPermissionKey(p.module, p.action))));
    setRoleDialogOpen(true);
  };

  const handleSaveRole = async () => {
    if (!roleName.trim()) {
      toast({ title: 'Role name is required', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      let roleId: string;

      if (selectedRole) {
        const { error } = await supabase
          .from('custom_roles')
          .update({ name: roleName, description: roleDescription })
          .eq('id', selectedRole.id);
        if (error) throw error;
        roleId = selectedRole.id;
        await supabase.from('role_permissions').delete().eq('role_id', roleId);
      } else {
        const { data, error } = await supabase
          .from('custom_roles')
          .insert({ name: roleName, description: roleDescription })
          .select()
          .single();
        if (error) throw error;
        roleId = data.id;
      }

      const permsToInsert = Array.from(selectedPermissions).map(key => {
        const [module, action] = key.split(':') as [Module, Action];
        return { role_id: roleId, module, action };
      });

      if (permsToInsert.length > 0) {
        const { error } = await supabase.from('role_permissions').insert(permsToInsert);
        if (error) throw error;
      }

      toast({ title: selectedRole ? 'Role updated' : 'Role created' });
      setRoleDialogOpen(false);
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error saving role', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async (role: CustomRole) => {
    if (role.is_system) {
      toast({ title: 'Cannot delete system roles', variant: 'destructive' });
      return;
    }
    if (!confirm(`Delete role "${role.name}"?`)) return;

    try {
      const { error } = await supabase.from('custom_roles').delete().eq('id', role.id);
      if (error) throw error;
      toast({ title: 'Role deleted' });
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error deleting role', description: error.message, variant: 'destructive' });
    }
  };

  // User handlers
  const openEditUser = (user: AppUser) => {
    setEditingUser(user);
    setUserFormData({
      full_name: user.full_name,
      role: user.role,
      is_active: user.is_active
    });
    setUserDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    
    setSaving(true);
    try {
      const { error } = await supabase
        .from('app_users')
        .update({
          full_name: userFormData.full_name,
          role: userFormData.role,
          is_active: userFormData.is_active
        })
        .eq('id', editingUser.id);

      if (error) throw error;
      toast({ title: 'User updated successfully' });
      setUserDialogOpen(false);
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateUser = async (userId: string) => {
    if (!confirm('Are you sure you want to deactivate this user?')) return;

    try {
      const { error } = await supabase
        .from('app_users')
        .update({ is_active: false })
        .eq('id', userId);

      if (error) throw error;
      toast({ title: 'User deactivated' });
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  // Role assignment handlers
  const handleAssignRole = async () => {
    if (!selectedUserId || !selectedRoleId) {
      toast({ title: 'Select both user and role', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from('user_custom_roles').insert({
        user_id: selectedUserId,
        role_id: selectedRoleId
      });
      if (error) throw error;
      toast({ title: 'Role assigned' });
      setAssignDialogOpen(false);
      setSelectedUserId('');
      setSelectedRoleId('');
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveUserRole = async (userRoleId: string) => {
    try {
      const { error } = await supabase.from('user_custom_roles').delete().eq('id', userRoleId);
      if (error) throw error;
      toast({ title: 'Role removed' });
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleAddUser = async () => {
    if (!newUserData.email || !newUserData.full_name) {
      toast({ title: 'Email and name are required', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          email: newUserData.email,
          full_name: newUserData.full_name,
          role: newUserData.role,
          password: newUserData.password || undefined
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({ title: 'User created successfully' });
      
      if (data?.tempPassword) {
        setCreatedUserPassword(data.tempPassword);
      } else {
        setAddUserDialogOpen(false);
        setNewUserData({ email: '', full_name: '', role: 'recruiter', password: '' });
      }
      
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error creating user', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const closeAddUserDialog = () => {
    setAddUserDialogOpen(false);
    setNewUserData({ email: '', full_name: '', role: 'recruiter', password: '' });
    setCreatedUserPassword(null);
  };

  // Password reset handlers
  const openResetPasswordDialog = (user: AppUser) => {
    setResetPasswordUser(user);
    setNewPassword('');
    setSendResetEmail(true);
    setGeneratedPassword(null);
    setPasswordCopied(false);
    setResetPasswordDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser) return;
    
    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-reset-password', {
        body: {
          user_id: resetPasswordUser.user_id,
          email: resetPasswordUser.email,
          full_name: resetPasswordUser.full_name,
          new_password: newPassword || undefined,
          send_email: sendResetEmail
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.newPassword) {
        setGeneratedPassword(data.newPassword);
        toast({ 
          title: 'Password reset successfully',
          description: data.emailSent 
            ? 'Email sent with new credentials' 
            : 'Please share the new password securely'
        });
      } else {
        toast({ title: 'Password updated successfully' });
        setResetPasswordDialogOpen(false);
      }
    } catch (error: any) {
      toast({ title: 'Error resetting password', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const copyPasswordToClipboard = async () => {
    if (generatedPassword) {
      await navigator.clipboard.writeText(generatedPassword);
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    }
  };

  const closeResetPasswordDialog = () => {
    setResetPasswordDialogOpen(false);
    setResetPasswordUser(null);
    setNewPassword('');
    setGeneratedPassword(null);
    setPasswordCopied(false);
  };

  const filteredUsers = users.filter(user => {
    const search = searchTerm.toLowerCase();
    return user.email.toLowerCase().includes(search) || 
           user.full_name.toLowerCase().includes(search);
  });

  const getUserRoles = (userId: string) => {
    return userRoles.filter(ur => ur.user_id === userId);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Users & Roles</h1>
        <p className="text-muted-foreground">Manage user accounts, roles, and granular permissions</p>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="roles" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Roles & Permissions
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setAddUserDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
              <Button variant="outline" onClick={() => setAssignDialogOpen(true)}>
                <UserPlus className="h-4 w-4 mr-2" />
                Assign Role
              </Button>
            </div>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Base Role</TableHead>
                  <TableHead>Custom Roles</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map(user => {
                  const assignedRoles = getUserRoles(user.id);
                  return (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.full_name}</TableCell>
                      <TableCell className="text-muted-foreground">{user.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{user.role}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {assignedRoles.map(ur => (
                            <Badge key={ur.id} variant="secondary" className="flex items-center gap-1">
                              {ur.custom_role?.name}
                              <button 
                                onClick={() => handleRemoveUserRole(ur.id)}
                                className="ml-1 hover:text-destructive"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                          {assignedRoles.length === 0 && (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.is_active ? 'default' : 'secondary'}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {new Date(user.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEditUser(user)} title="Edit user">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openResetPasswordDialog(user)} title="Reset password">
                            <KeyRound className="h-4 w-4" />
                          </Button>
                          {user.is_active && (
                            <Button variant="ghost" size="icon" onClick={() => handleDeactivateUser(user.id)} title="Deactivate user">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Roles Tab */}
        <TabsContent value="roles" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={openNewRole}>
              <Plus className="h-4 w-4 mr-2" />
              Create Role
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Role Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Permissions</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.map(role => {
                  const rolePerms = getRolePermissions(role.id);
                  const permSummary = MODULES.map(module => {
                    const moduleActions = ACTIONS.filter(a => hasPermission(role.id, module, a));
                    if (moduleActions.length === 0) return null;
                    return { module, actions: moduleActions };
                  }).filter(Boolean);

                  return (
                    <TableRow key={role.id}>
                      <TableCell className="font-medium">{role.name}</TableCell>
                      <TableCell className="text-muted-foreground max-w-[200px] truncate">
                        {role.description || '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={role.is_system ? 'secondary' : 'outline'}>
                          {role.is_system ? 'System' : 'Custom'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-[300px]">
                          {permSummary.slice(0, 3).map((p: any) => (
                            <Badge key={p.module} variant="outline" className="text-xs">
                              {p.module}: {p.actions.length === 4 ? 'All' : p.actions.join(', ')}
                            </Badge>
                          ))}
                          {permSummary.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{permSummary.length - 3} more
                            </Badge>
                          )}
                          {permSummary.length === 0 && (
                            <span className="text-muted-foreground text-sm">No permissions</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEditRole(role)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {!role.is_system && (
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteRole(role)}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Role Edit/Create Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedRole ? 'Edit Role' : 'Create Role'}</DialogTitle>
            <DialogDescription>
              {selectedRole ? 'Modify role details and permissions' : 'Create a new role with specific permissions'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="roleName">Role Name</Label>
                <Input
                  id="roleName"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  placeholder="e.g., Senior Recruiter"
                  disabled={selectedRole?.is_system}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="roleDescription">Description</Label>
                <Textarea
                  id="roleDescription"
                  value={roleDescription}
                  onChange={(e) => setRoleDescription(e.target.value)}
                  placeholder="Describe what this role can do..."
                />
              </div>
            </div>

            <div className="space-y-4">
              <Label>Permissions</Label>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Module</TableHead>
                    {ACTIONS.map(action => (
                      <TableHead key={action} className="text-center capitalize w-[80px]">{action}</TableHead>
                    ))}
                    <TableHead className="text-center w-[60px]">All</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {MODULES.map(module => {
                    const moduleKeys = ACTIONS.map(a => getPermissionKey(module, a));
                    const allSelected = moduleKeys.every(k => selectedPermissions.has(k));
                    
                    return (
                      <TableRow key={module}>
                        <TableCell className="font-medium capitalize">{module}</TableCell>
                        {ACTIONS.map(action => (
                          <TableCell key={action} className="text-center">
                            <Checkbox
                              checked={selectedPermissions.has(getPermissionKey(module, action))}
                              onCheckedChange={() => togglePermission(module, action)}
                            />
                          </TableCell>
                        ))}
                        <TableCell className="text-center">
                          <Checkbox
                            checked={allSelected}
                            onCheckedChange={() => toggleModuleAll(module)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveRole} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {selectedRole ? 'Update Role' : 'Create Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Edit Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={editingUser?.email || ''} disabled />
            </div>
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={userFormData.full_name}
                onChange={(e) => setUserFormData({ ...userFormData, full_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Base Role</Label>
              <Select
                value={userFormData.role}
                onValueChange={(value: any) => setUserFormData({ ...userFormData, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="recruiter">Recruiter</SelectItem>
                  <SelectItem value="analyst">Analyst</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label>Active Status</Label>
              <Switch
                checked={userFormData.is_active}
                onCheckedChange={(checked) => setUserFormData({ ...userFormData, is_active: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveUser} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Role Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Role to User</DialogTitle>
            <DialogDescription>Select a user and a role to assign</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>User</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  {users.filter(u => u.is_active).map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.full_name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(role => (
                    <SelectItem key={role.id} value={role.id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAssignRole} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Assign Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={addUserDialogOpen} onOpenChange={(open) => !open && closeAddUserDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{createdUserPassword ? 'User Created' : 'Add New User'}</DialogTitle>
            <DialogDescription>
              {createdUserPassword 
                ? 'Share the temporary password securely with the new user'
                : 'Create a new user account'}
            </DialogDescription>
          </DialogHeader>
          
          {createdUserPassword ? (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <Label className="text-sm text-muted-foreground">Temporary Password</Label>
                <div className="font-mono text-lg bg-background p-2 rounded border">
                  {createdUserPassword}
                </div>
                <p className="text-xs text-muted-foreground">
                  Please share this password securely with the user. They should change it on first login.
                </p>
              </div>
              <DialogFooter>
                <Button onClick={closeAddUserDialog}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="newUserEmail">Email *</Label>
                  <Input
                    id="newUserEmail"
                    type="email"
                    value={newUserData.email}
                    onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                    placeholder="user@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newUserName">Full Name *</Label>
                  <Input
                    id="newUserName"
                    value={newUserData.full_name}
                    onChange={(e) => setNewUserData({ ...newUserData, full_name: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newUserRole">Role</Label>
                  <Select
                    value={newUserData.role}
                    onValueChange={(value: any) => setNewUserData({ ...newUserData, role: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="recruiter">Recruiter</SelectItem>
                      <SelectItem value="analyst">Analyst</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newUserPassword">Password (optional)</Label>
                  <Input
                    id="newUserPassword"
                    type="password"
                    value={newUserData.password}
                    onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                    placeholder="Leave blank for auto-generated"
                  />
                  <p className="text-xs text-muted-foreground">
                    If left blank, a temporary password will be generated
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeAddUserDialog}>Cancel</Button>
                <Button onClick={handleAddUser} disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Create User
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={(open) => !open && closeResetPasswordDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Reset password for {resetPasswordUser?.full_name} ({resetPasswordUser?.email})
            </DialogDescription>
          </DialogHeader>
          
          {generatedPassword ? (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <Label className="text-sm text-muted-foreground">New Password</Label>
                <div className="flex items-center gap-2 mt-2">
                  <code className="flex-1 bg-background px-3 py-2 rounded font-mono text-sm">
                    {generatedPassword}
                  </code>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={copyPasswordToClipboard}
                    title="Copy to clipboard"
                  >
                    {passwordCopied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              {sendResetEmail && (
                <p className="text-sm text-muted-foreground">
                  ✓ Email with new credentials has been sent to the user.
                </p>
              )}
              <DialogFooter>
                <Button onClick={closeResetPasswordDialog}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPasswordInput">New Password (optional)</Label>
                <Input
                  id="newPasswordInput"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Leave blank to auto-generate"
                />
                <p className="text-xs text-muted-foreground">
                  If left blank, a secure password will be generated automatically.
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="sendResetEmail"
                  checked={sendResetEmail}
                  onCheckedChange={(checked) => setSendResetEmail(checked === true)}
                />
                <Label htmlFor="sendResetEmail" className="text-sm cursor-pointer">
                  Send email with new credentials
                </Label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeResetPasswordDialog}>Cancel</Button>
                <Button onClick={handleResetPassword} disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Reset Password
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserRoleManagement;
